/**
 * Whether the model library is folded away, what the button that folds it should say, and
 * which of the two shapes the library is currently in.
 *
 * Pure and in the domain layer for the same reason the theme is: what the button says, and
 * what a stored preference comes to, are decisions about the preference rather than about
 * the DOM, and they are the parts worth getting right. The UI module is left with an
 * attribute, a `localStorage` key, a media query and a click handler.
 *
 * The state is a plain boolean, so nothing here resolves anything the way a theme resolves
 * `system`. What it does do is keep the wording in one place, because a control that hides
 * something has to say which way it is about to go rather than which way it currently is —
 * a button reading "Collapsed" leaves you guessing whether that is a description or an
 * instruction.
 *
 * The layout is here for the same reason. The library is a column beside the view on a wide
 * screen and a band above it on a narrow one, and folding means something different in each:
 * a column narrows to a strip at the side, a band concertinas up out of the way. The arrow
 * on the button has to travel the way the edge travels, so it cannot be one fixed pair of
 * glyphs — and which way a page with no stored preference should start differs too.
 */

/** Where the choice is kept between visits. */
export const SIDEBAR_STORAGE_KEY = '3dpreview:sidebar';

/**
 * The value written for a folded-away library.
 *
 * Written out rather than stored as `'true'`, so that a look at the stored value says what
 * it means. Only this one word counts as collapsed; anything else reads as open.
 */
const COLLAPSED = 'collapsed';

/** And the value written for a library left showing, which is equally its own word. */
const OPEN = 'open';

/**
 * Which shape the library is in.
 *
 * `column` is the wide layout — the library beside the view, folding sideways. `band` is
 * the narrow one — the library above the view, folding upwards.
 */
export type SidebarLayout = 'column' | 'band';

/**
 * The window width below which the library becomes a band.
 *
 * The same 48rem the stylesheet switches on, and named here so the two cannot drift: a
 * button drawn with the arrow for one shape while the page is laid out in the other points
 * the wrong way, which is worse than pointing nowhere.
 */
export const SIDEBAR_BAND_QUERY = '(width < 48rem)';

/**
 * Read back a stored choice, taking anything unrecognised as an open library.
 *
 * Takes `string | null` because that is what `localStorage.getItem` gives.
 */
export function parseSidebarCollapsed(value: string | null | undefined): boolean {
  return value === COLLAPSED;
}

/** What to store for a given state. */
export function sidebarStorageValue(collapsed: boolean): string {
  return collapsed ? COLLAPSED : OPEN;
}

/**
 * The state to come up in, given what was stored and the shape the page is in.
 *
 * A stored choice wins outright, in either shape: somebody who folded the library on a
 * phone and then opened the same browser on a monitor asked for it folded, and the two
 * layouts are the same application rather than two applications.
 *
 * With nothing stored the shape decides, and the two shapes want opposite things. A column
 * beside a view costs a quarter of a wide window and pays for itself by showing the
 * library, so it starts open. A band above a view on a phone costs half the screen, which
 * is most of what there is to see with, so it starts folded — arriving on a phone should
 * mean arriving at the viewer.
 *
 * Anything unrecognised — a value written by an older version, or edited by hand — falls
 * through to the same default rather than being trusted, and both defaults leave the fold
 * button exactly where it always is, so neither can strand anybody.
 */
export function initialSidebarCollapsed(
  stored: string | null | undefined,
  layout: SidebarLayout,
): boolean {
  if (stored === COLLAPSED) return true;
  if (stored === OPEN) return false;
  return layout === 'band';
}

/** What the button shows, and what it tells anyone who cannot see the arrow on it. */
export interface SidebarLabel {
  /** The glyph on the button: which way the library is about to move. */
  readonly text: string;
  /** The accessible name and the tooltip. Says what the press will do, not what is so. */
  readonly description: string;
}

/**
 * Label the button, given what the library is doing now and which shape it is in.
 *
 * The arrow points the way the edge will travel, which is the one convention that reads the
 * same whether you take it as "the panel goes that way" or as "press to reveal what is that
 * way". Sideways for the column, upwards and downwards for the band, because an arrow that
 * points left on a panel that moves up is not a hint but a contradiction.
 *
 * The description names the outcome — hide, show — because an arrow read aloud is nothing
 * at all, and it says what the fold is *for*, which differs: sideways the fold buys width,
 * upwards it buys the greater part of a small screen.
 */
export function describeSidebarToggle(
  collapsed: boolean,
  layout: SidebarLayout = 'column',
): SidebarLabel {
  if (collapsed) {
    return {
      text: layout === 'band' ? '▾' : '›',
      description: 'Show the model library',
    };
  }

  return {
    text: layout === 'band' ? '▴' : '‹',
    description:
      layout === 'band'
        ? 'Hide the model library, for more of the view'
        : 'Hide the model library, for a wider view',
  };
}
